import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import SessionExpiredModal from '../components/SessionExpiredModal';
import { clearDevicePush } from '../lib/pushClient';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Resiliencia de sesión (Feb 2026)
//
// El backend emite:
//   - access_token (cookie HttpOnly, max_age=900s = 15 min)
//   - refresh_token (cookie HttpOnly, max_age=7 días)
//
// Problema previo: cualquier petición después de 15 min de inactividad
// recibía 401 "Not authenticated" y el usuario perdía su trabajo. No había
// renovación automática ni retry.
//
// Solución (3 capas defensivas):
//   1) Timer proactivo cada 12 min → POST /auth/refresh en silencio.
//   2) Response interceptor en 401 → intenta /auth/refresh y reintenta la
//      petición original UNA vez. Dedupe de refresh concurrente con una
//      Promise compartida para evitar tormentas si el usuario dispara
//      varias peticiones a la vez.
//   3) Si el refresh también falla → se muestra `SessionExpiredModal`
//      (sin redirect ni reload), que permite volver a loguearse en el
//      mismo contexto manteniendo la URL actual.
// ---------------------------------------------------------------------------

const PROACTIVE_REFRESH_MS = 12 * 60 * 1000; // 12 min (< 15 min TTL del access)
const REFRESH_URL_FRAGMENTS = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/logout'];

// Promesa compartida de refresh para deduplicar peticiones concurrentes.
let _refreshInFlight = null;

const doRefresh = () => {
  if (!_refreshInFlight) {
    _refreshInFlight = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Ref necesaria porque el interceptor se registra una sola vez y necesita
  // leer el user actual sin re-registrarse en cada render.
  const hasBeenAuthRef = useRef(false);
  const proactiveTimerRef = useRef(null);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      hasBeenAuthRef.current = true;
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // Registra axios response interceptor UNA sola vez por app.
  // ------------------------------------------------------------------
  useEffect(() => {
    // Cada setup instala su propio interceptor; StrictMode ejecuta cleanup y setup.
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        if (!error.response || error.response.status !== 401 || !original) {
          return Promise.reject(error);
        }
        const url = original.url || '';
        // No reintentes en los propios endpoints de auth — evita bucles.
        if (REFRESH_URL_FRAGMENTS.some((f) => url.includes(f))) {
          return Promise.reject(error);
        }
        // No reintentes dos veces la misma request.
        if (original._retry) {
          return Promise.reject(error);
        }
        original._retry = true;

        try {
          await doRefresh();
        } catch (refreshErr) {
          // Si teníamos sesión abierta, mostramos el modal (sin redirigir).
          // Si nunca llegó a autenticarse (ej. landing en /login), callamos
          // el error — es un 401 limpio pre-login.
          if (hasBeenAuthRef.current) {
            setSessionExpired(true);
          }
          return Promise.reject(error);
        }
        // Reintenta la petición original con la nueva cookie.
        try {
          return await axios(original);
        } catch (retryErr) {
          return Promise.reject(retryErr);
        }
      },
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ------------------------------------------------------------------
  // Timer proactivo: refresca el access_token cada 12 min mientras el
  // usuario esté autenticado. Se cancela en logout / sessionExpired.
  // ------------------------------------------------------------------
  useEffect(() => {
    const authed = !!user && user !== false;
    if (!authed || sessionExpired) {
      if (proactiveTimerRef.current) {
        clearInterval(proactiveTimerRef.current);
        proactiveTimerRef.current = null;
      }
      return;
    }
    if (proactiveTimerRef.current) return;
    proactiveTimerRef.current = setInterval(() => {
      doRefresh().catch(() => {
        // Si el refresh programado falla, dejamos que el próximo 401 real
        // dispare el modal — no mostramos UI de error silenciosamente.
      });
    }, PROACTIVE_REFRESH_MS);
    return () => {
      if (proactiveTimerRef.current) {
        clearInterval(proactiveTimerRef.current);
        proactiveTimerRef.current = null;
      }
    };
  }, [user, sessionExpired]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    hasBeenAuthRef.current = true;
    setSessionExpired(false);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
    setUser(response.data);
    hasBeenAuthRef.current = true;
    setSessionExpired(false);
    return response.data;
  };

  const logout = async () => {
    try {
      await clearDevicePush().catch(() => {});
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore errors
    }
    hasBeenAuthRef.current = false;
    setSessionExpired(false);
    setUser(false);
  };

  const processGoogleSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(response.data);
    hasBeenAuthRef.current = true;
    setSessionExpired(false);
    return response.data;
  };

  // Legacy: refresco manual (sigue disponible para componentes que lo invoquen).
  const refreshToken = async () => {
    try {
      await doRefresh();
      await checkAuth();
    } catch {
      if (hasBeenAuthRef.current) {
        setSessionExpired(true);
      } else {
        setUser(false);
      }
    }
  };

  // Permite al modal re-autenticar sin perder la URL ni los datos en memoria.
  const resolveSessionExpired = async (email, password) => {
    await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // Refresca el objeto user por si cambió algo en servidor entre tanto.
    const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
    setUser(me.data);
    hasBeenAuthRef.current = true;
    setSessionExpired(false);
    return me.data;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    processGoogleSession,
    refreshToken,
    checkAuth,
    isAuthenticated: !!user && user !== false,
    sessionExpired,
    resolveSessionExpired,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {sessionExpired && (
        <SessionExpiredModal
          currentEmail={typeof user === 'object' && user?.email ? user.email : ''}
          onResolve={resolveSessionExpired}
        />
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;
